// lib/stripe/couponUtils.ts

import Stripe from "stripe";

/**
 * Represents the result of a successful coupon validation.
 */
export interface ValidatedCouponDetails {
  couponId: string;
  /** The percentage discount, or null if it's a fixed amount coupon or validation failed. */
  percentOff: number | null;
}

/**
 * Validates a string as either an active Stripe Promotion Code or a valid Coupon ID.
 * MUST be used server-side as it requires the Stripe secret key.
 *
 * @param {string | undefined | null} codeOrId - The string to validate (customer-facing code or internal Coupon ID).
 * @param {Stripe} stripe - An initialized Stripe instance (using your secret key).
 * @returns {Promise<ValidatedCouponDetails | null>} A Promise resolving to an object with coupon details if valid, otherwise null.
 */
export async function validateStripeCoupon(
  codeOrId: string | undefined | null,
  stripe: Stripe
): Promise<ValidatedCouponDetails | null> {
  // <-- Updated return type
  if (!codeOrId || typeof codeOrId !== "string") {
    console.log("No code or ID provided for validation.");
    return null;
  }

  const trimmedCode = codeOrId.trim();
  if (!trimmedCode) {
    console.log("Empty code or ID provided after trimming.");
    return null;
  }

  console.log(`Attempting to validate code/ID: ${trimmedCode}`);

  try {
    // --- Step 1: Try as an active Promotion Code ---
    const promotionCodes = await stripe.promotionCodes.list({
      code: trimmedCode,
      active: true,
      limit: 1,
      expand: ["data.coupon"], // Crucial: fetch the associated coupon object
    });

    if (promotionCodes.data.length > 0) {
      const promoCode = promotionCodes.data[0];
      const couponData = promoCode?.coupon; // The expanded coupon object

      // Ensure the expanded coupon and its ID exist and the coupon itself is valid
      if (couponData?.id && couponData?.valid) {
        const percentOff = couponData.percent_off ?? null; // Get percent_off (will be null for amount coupons)
        console.log(
          `Validated as active Promotion Code "${trimmedCode}". Coupon ID: ${couponData.id}, Percent Off: ${percentOff}`
        );
        return {
          // <-- Return object
          couponId: couponData.id,
          percentOff: percentOff,
        };
      } else {
        console.warn(
          `Promotion Code "${trimmedCode}" found but its associated coupon is invalid or missing.`
        );
        return null; // Don't proceed if the linked coupon is bad
      }
    }

    // --- Step 2: If not found as active Promo Code, try as a direct Coupon ID ---
    console.log(
      `Code "${trimmedCode}" not found as active Promotion Code. Trying as Coupon ID...`
    );
    try {
      const coupon = await stripe.coupons.retrieve(trimmedCode);

      // Check if the retrieved coupon is valid
      if (coupon && coupon.valid) {
        const percentOff = coupon.percent_off ?? null; // Get percent_off
        console.log(
          `Validated directly as Coupon ID: ${coupon.id}, Percent Off: ${percentOff}`
        );
        return {
          // <-- Return object
          couponId: coupon.id,
          percentOff: percentOff,
        };
      } else {
        console.log(
          `Coupon ID "${trimmedCode}" retrieved but is marked invalid.`
        );
        return null; // Found but not valid
      }
    } catch (couponError: any) {
      if (couponError.code === "resource_missing") {
        console.log(
          `Code "${trimmedCode}" is not a valid Coupon ID (resource_missing).`
        );
      } else {
        console.error(
          `Stripe API error retrieving Coupon ID "${trimmedCode}":`,
          couponError.message || couponError
        );
      }
      return null; // Failed retrieval
    }
  } catch (error: any) {
    console.error(
      `Unexpected error validating coupon/promotion code "${trimmedCode}":`,
      error.message || error
    );
    return null; // General validation failure
  }
}
